import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, Expand } from "lucide-react";
import { Reveal, SectionTag } from "./Reveal";

const photos = [
  { src: "https://images.pexels.com/photos/430208/pexels-photo-430208.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940", cat: "CCTV", label: "Wall-mounted bullet cam — residence, Mandoli" },
  { src: "https://images.unsplash.com/photo-1618482914248-29272d021005?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NzR8MHwxfHNlYXJjaHxfHxzZWN1cml0eSUyMGNhbWVyYXxlbnwwfHx8fDE3ODg3MTM1Njl8MA&ixlib=rb-4.1.0&q=85", cat: "CCTV", label: "Multi-camera pole array — commercial site" },
  { src: "https://images.pexels.com/photos/5966513/pexels-photo-5966513.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940", cat: "CCTV", label: "Dome camera — shop interior, Ghaziabad" },
  { src: "https://images.pexels.com/photos/7522609/pexels-photo-7522609.jpeg", cat: "Biometric", label: "Fingerprint access terminal — office entry" },
  { src: "https://images.unsplash.com/photo-1585079374502-415f8516dcc3", cat: "Biometric", label: "Biometric scanner calibration" },
  { src: "https://images.unsplash.com/photo-1558002038-1055907df827?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1Nzd8MHwxfHNlYXJjaHwxfHxzbWFydCUyMGxvY2slMjBkb29yfGVufDB8fHx8MTc4ODcxMzU2OXww&ixlib=rb-4.1.0&q=85", cat: "Locks", label: "Smart door lock install — apartment" },
];

const filters = ["All", "CCTV", "Biometric", "Locks"];

export default function Gallery() {
  const [filter, setFilter] = useState("All");
  const [active, setActive] = useState(null);
  const shown = filter === "All" ? photos : photos.filter((p) => p.cat === filter);

  return (
    <section id="gallery" className="relative py-28 sm:py-36 bg-base" data-testid="gallery-section">
      <div className="max-w-7xl mx-auto px-5 sm:px-8">
        <Reveal>
          <SectionTag index="03" label="Proof of work" />
          <div className="flex flex-wrap items-end justify-between gap-6">
            <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight max-w-xl">
              Installed. Aligned. <span className="text-cyan-400">Recording.</span>
            </h2>
            <div className="flex gap-2 flex-wrap" data-testid="gallery-filters">
              {filters.map((f) => (
                <button
                  key={f}
                  onClick={() => setFilter(f)}
                  data-testid={`gallery-filter-${f.toLowerCase()}`}
                  className={`font-mono2 text-xs uppercase tracking-[0.15em] px-4 py-2 rounded-full border transition-all duration-300 ${
                    filter === f
                      ? "border-cyan-400 bg-cyan-500/10 text-cyan-300"
                      : "border-line text-soft hover:border-line-strong"
                  }`}
                >
                  {f}
                </button>
              ))}
            </div>
          </div>
        </Reveal>

        <motion.div layout className="mt-14 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          <AnimatePresence mode="popLayout">
            {shown.map((p, i) => (
              <motion.button
                layout
                key={p.src}
                initial={{ opacity: 0, scale: 0.94 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.94 }}
                transition={{ duration: 0.5, delay: i * 0.05, ease: [0.16, 1, 0.3, 1] }}
                onClick={() => setActive(p)}
                data-testid={`gallery-item-${i}`}
                className="group relative clip-frame overflow-hidden text-left aspect-[4/3] bg-card"
              >
                <img
                  src={p.src}
                  alt={p.label}
                  loading="lazy"
                  className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                />
                <div className="absolute inset-0 img-overlay opacity-80" />
                <div className="absolute bottom-0 inset-x-0 p-5 flex items-end justify-between gap-3">
                  <span className="text-xs text-body leading-snug">{p.label}</span>
                  <Expand className="w-4 h-4 text-cyan-400 shrink-0 opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                </div>
              </motion.button>
            ))}
          </AnimatePresence>
        </motion.div>
      </div>

      <AnimatePresence>
        {active && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[80] bg-base/95 backdrop-blur-md flex items-center justify-center p-5"
            onClick={() => setActive(null)}
            data-testid="gallery-lightbox"
          >
            <motion.div
              initial={{ scale: 0.9, y: 30 }}
              animate={{ scale: 1, y: 0 }}
              exit={{ scale: 0.92, y: 20 }}
              transition={{ type: "spring", damping: 24, stiffness: 220 }}
              className="relative max-w-4xl w-full"
              onClick={(e) => e.stopPropagation()}
            >
              <img src={active.src} alt={active.label} className="w-full max-h-[75vh] object-contain rounded-xl" />
              <p className="mt-4 text-center font-mono2 text-xs tracking-[0.15em] text-soft uppercase">{active.label}</p>
              <button
                onClick={() => setActive(null)}
                data-testid="lightbox-close-button"
                className="absolute -top-3 -right-3 w-10 h-10 rounded-full bg-cyan-500 text-on-accent flex items-center justify-center hover:bg-cyan-400 transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
}
